/* global axios */

// Environment variables
// In production, use Rails proxy; in development, can use Vite proxy
const USE_RAILS_PROXY =
  import.meta.env.PROD || import.meta.env.VITE_USE_RAILS_PROXY === 'true';

const CEA_REST_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/rest'
  : import.meta.env.VITE_CEA_REST_URL || '/ceadevws/';

const CEA_SOAP_CONTRACT_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/soap/InterfazGenericaContratacionWS'
  : import.meta.env.VITE_CEA_SOAP_CONTRACT_URL ||
    '/aquacis-cea/services/InterfazGenericaContratacionWS';

const CEA_SOAP_WORKORDER_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/soap/InterfazGenericaOrdenesServicioWS'
  : import.meta.env.VITE_CEA_SOAP_WORKORDER_URL ||
    '/aquacis-cea/services/InterfazGenericaOrdenesServicioWS';

const CEA_SOAP_METER_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/soap/InterfazGenericaContadoresWS'
  : import.meta.env.VITE_CEA_SOAP_METER_URL ||
    '/aquacis-cea/services/InterfazGenericaContadoresWS';

const CEA_SOAP_DEBT_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/soap/InterfazGenericaGestionDeudaWS'
  : import.meta.env.VITE_CEA_SOAP_DEBT_URL ||
    '/aquacis-cea/services/InterfazGenericaGestionDeudaWS';

const CEA_SOAP_READINGS_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/soap/InterfazOficinaVirtualClientesWS'
  : import.meta.env.VITE_CEA_SOAP_READINGS_URL ||
    '/aquacis-cea/services/InterfazOficinaVirtualClientesWS';

const CEA_SOAP_RECEIPT_URL = USE_RAILS_PROXY
  ? '/api/v1/cea/soap/InterfazOficinaVirtualClientesWS'
  : import.meta.env.VITE_CEA_SOAP_RECEIPT_URL ||
    '/aquacis-cea/services/InterfazOficinaVirtualClientesWS';


const CEA_API_USERNAME =
  import.meta.env.VITE_CEA_API_USERNAME || 'WSGESTIONDEUDA';
const CEA_API_PASSWORD =
  import.meta.env.VITE_CEA_API_PASSWORD || 'WSGESTIONDEUDA';

// ============================================================================
// XML Helper Functions
// ============================================================================

/**
 * Parse XML response string into a Document object
 * @param {string} xmlString - XML string to parse
 * @returns {Document} Parsed XML document
 */
const parseXmlResponse = xmlString => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  // Basic error checking
  const parserError = xmlDoc.getElementsByTagName('parsererror');
  if (parserError.length > 0) {
    throw new Error('Error parsing XML response');
  }
  return xmlDoc;
};

/**
 * Safely escape XML special characters
 * @param {unknown} value - Value to escape
 * @returns {string} Escaped string
 */
export const xmlEscape = value => {
  if (value === undefined || value === null) return '';
  const s = String(value);
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Convert XML Element or Document to a JSON-friendly object
 * Handles xsi:nil and repeated elements (arrays)
 * @param {Document|Element} node - XML node to convert
 * @returns {any} JSON object
 */
/* eslint-disable no-use-before-define */
export const xmlToJson = node => {
  const parseChild = child => {
    // Check for xsi:nil attribute (namespace aware)
    const xsiNil =
      child.getAttributeNS &&
      child.getAttributeNS('http://www.w3.org/2001/XMLSchema-instance', 'nil');
    if (xsiNil === 'true' || xsiNil === '1') return null;

    if (child.children.length === 0) {
      const text = child.textContent;
      if (text === null || text.trim() === '') return null;
      return text.trim();
    }
    return parseElement(child);
  };

  const parseElement = el => {
    const obj = {};
    const children = Array.from(el.children);

    // Group children by tag name to detect repeated fields (arrays)
    const groups = {};
    children.forEach(c => {
      const name = c.localName || c.nodeName;
      groups[name] = groups[name] || [];
      groups[name].push(c);
    });

    Object.entries(groups).forEach(([name, elems]) => {
      if (elems.length > 1) {
        obj[name] = elems.map(child => parseChild(child));
      } else {
        obj[name] = parseChild(elems[0]);
      }
    });

    return obj;
  };

  if (node.documentElement) {
    const doc = node;
    return parseElement(doc.documentElement);
  }
  return parseElement(node);
};
/* eslint-enable no-use-before-define */

// ============================================================================
// SOAP Request Helper
// ============================================================================

/**
 * Extract human-readable fault message from SOAP XML error response
 * @param {string} body - Raw SOAP XML response body
 * @returns {string} Extracted fault string or truncated body
 */
export const extractSoapFaultString = body => {
  if (!body || typeof body !== 'string') return '';
  const faultMatch = body.match(/<faultstring>([^<]+)<\/faultstring>/);
  if (faultMatch) return faultMatch[1].trim();
  const detailMatch = body.match(/<detail>([^<]+)<\/detail>/);
  if (detailMatch) return detailMatch[1].trim();
  return body.length > 200 ? `${body.substring(0, 200)}…` : body;
};

const RETRY_STATUS_CODES = new Set([500, 502, 503, 504]);
const MAX_RETRIES = 2;
const BASE_DELAY_MS = 1500;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Generic SOAP request helper with retry for transient errors
 * @param {string} url - SOAP endpoint URL
 * @param {string} soapAction - SOAP action header
 * @param {string} xmlBody - SOAP XML body
 * @returns {Promise<Document>} Parsed XML response
 */
const sendSoapRequest = async (url, soapAction, xmlBody) => {
  let lastError = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      if (attempt > 0) {
        await sleep(BASE_DELAY_MS * attempt);
      }

      /* eslint-disable no-await-in-loop */
      const response = await axios.post(url, xmlBody, {
        headers: {
          'Content-Type': 'text/xml;charset=UTF-8',
          SOAPAction: soapAction,
        },
        validateStatus: () => true,
        timeout: 30000,
      });
      /* eslint-enable no-await-in-loop */

      if (response.status >= 400) {
        const body =
          typeof response.data === 'string'
            ? response.data
            : JSON.stringify(response.data);

        const isRetryable = RETRY_STATUS_CODES.has(response.status);
        if (isRetryable && attempt < MAX_RETRIES) {
          lastError = { status: response.status, body };
          continue;
        }

        const faultMessage = extractSoapFaultString(body);
        const err = new Error(
          `Error de Aquasis: SOAP request failed with status ${response.status}. Response: ${faultMessage}`
        );
        err.response = response;
        err.aquasisFault = faultMessage;
        err.httpStatus = response.status;
        throw err;
      }

      return parseXmlResponse(response.data);
    } catch (error) {
      if (error.aquasisFault) throw error;

      const isTimeout =
        error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isNetworkError = !error.response && !error.aquasisFault;

      if ((isTimeout || isNetworkError) && attempt < MAX_RETRIES) {
        lastError = error;
        continue;
      }

      if (error?.response) {
        const resp = error.response;
        const body =
          typeof resp.data === 'string'
            ? resp.data
            : JSON.stringify(resp.data);
        const faultMessage = extractSoapFaultString(body);
        const enhanced = new Error(
          `Error de Aquasis: SOAP request failed with status ${resp.status}. Response: ${faultMessage}`
        );
        enhanced.response = resp;
        enhanced.aquasisFault = faultMessage;
        enhanced.httpStatus = resp.status;
        throw enhanced;
      }
      throw error;
    }
  }

  const fallbackMsg = lastError?.body || lastError?.message || 'Unknown error';
  throw new Error(
    `Aquasis no responde después de ${MAX_RETRIES + 1} intentos: ${fallbackMsg}`
  );
};

// ============================================================================
// REST API Functions
// ============================================================================

/**
 * Update case to closed status
 * @param {string} caseId - Case ID
 * @param {string} code - Status code
 * @param {string} note - Notes
 * @returns {Promise<any>} Response data
 */
export const updateCaseToClosed = async (caseId, code, note) => {
  const url = CEA_REST_URL;
  const data = {
    evento: 'terminar_reporte_caso',
    data: {
      caso_sn: caseId,
      sn_code: code,
      sn_notes: note,
      sys_id: '',
      orden_aquacis: '',
    },
  };

  const response = await axios.put(url, data);
  return response.data;
};

/**
 * Reference work order from Aquacis
 * @param {string} caseId - Case ID
 * @param {string} workOrderId - Work order ID
 * @returns {Promise<any>} Response data
 */
export const referenceWorkOrderAquacis = async (caseId, workOrderId) => {
  const url = CEA_REST_URL;
  const data = {
    evento: 'asigna_orden_aquacis',
    data: {
      sys_id: caseId,
      orden_aquacis: workOrderId,
      caso_sn: '',
      sn_code: '',
      sn_notes: '',
    },
  };

  const response = await axios.put(url, data);
  return response.data;
};

/**
 * Update case to cancelled status
 * @param {string} caseId - Case ID
 * @returns {Promise<any>} Response data
 */
export const updateCaseToCancelled = async caseId => {
  const url = CEA_REST_URL;
  const data = {
    evento: 'anular_reporte_caso',
    data: {
      caso_sn: caseId,
      sys_id: '',
      orden_aquacis: '',
      sn_code: '',
      sn_notes: '',
    },
  };

  const response = await axios.put(url, data);
  return response.data;
};

// ============================================================================
// SOAP API Functions - Contracts
// ============================================================================

/**
 * CEA ConsultaDetalleContrato
 * @param {string} numeroContrato - Contract number
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const consultaDetalleContrato = async (
  numeroContrato,
  idioma = 'es'
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com">
   <soapenv:Header/>
   <soapenv:Body>
      <occ:consultaDetalleContrato>
      <numeroContrato>${xmlEscape(numeroContrato)}</numeroContrato>
      <idioma>${xmlEscape(idioma)}</idioma>
      </occ:consultaDetalleContrato>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_CONTRACT_URL, '', xml);
};

/**
 * Get contract detail as JSON
 * @param {string} numeroContrato - Contract number
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<any>} JSON object
 */
export const consultaDetalleContratoJson = async (
  numeroContrato,
  idioma = 'es'
) => {
  const xmlDoc = await consultaDetalleContrato(numeroContrato, idioma);
  const returnElement =
    xmlDoc.getElementsByTagName('consultaDetalleContratoReturn')[0] ||
    xmlDoc.getElementsByTagName('consultaDetalleContratoResponse')[0];
  if (returnElement) {
    return xmlToJson(returnElement);
  }
  return xmlToJson(xmlDoc);
};

/**
 * Get contract information
 * @param {string} numContrato - Contract number
 * @param {string} idioma - Language (default: 'es')
 * @param {string} opciones - Options (default: '')
 * @returns {Promise<Document>} XML document
 */
export const getContrato = async (
  numContrato,
  idioma = 'es',
  opciones = ''
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com">
   <soapenv:Header/>
   <soapenv:Body>
      <occ:getContrato>
      <numContrato>${xmlEscape(numContrato)}</numContrato>
      <idioma>${xmlEscape(idioma)}</idioma>
      <opciones>${xmlEscape(opciones)}</opciones>
      </occ:getContrato>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_CONTRACT_URL, '', xml);
};

/**
 * Search contracts with filters
 * @param {string} numeroContrato - Contract number
 * @param {string} actividad - Activity
 * @param {string} actividadSectorial - Sector activity
 * @param {string} uso - Usage
 * @param {string} cnaeDesde - CNAE from
 * @param {string} cnaeHasta - CNAE to
 * @param {string[]} estados - States array
 * @returns {Promise<Document>} XML document
 */
export const getContratos = async (
  numeroContrato,
  actividad,
  actividadSectorial,
  uso,
  cnaeDesde,
  cnaeHasta,
  estados
) => {
  const estadosXml = estados
    .map(e => `<string>${xmlEscape(e)}</string>`)
    .join('');
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <occ:getContratos>
         <numeroContrato>${xmlEscape(numeroContrato)}</numeroContrato>
         <actividad>${xmlEscape(actividad)}</actividad>
         <actividadSectorial>${xmlEscape(actividadSectorial)}</actividadSectorial>
         <uso>${xmlEscape(uso)}</uso>
         <cnaeDesde>${xmlEscape(cnaeDesde)}</cnaeDesde>
         <cnaeHasta>${xmlEscape(cnaeHasta)}</cnaeHasta>
         <estados>
            ${estadosXml}
         </estados>
      </occ:getContratos>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_CONTRACT_URL, '', xml);
};

// ============================================================================
// SOAP API Functions - Work Orders
// ============================================================================

/**
 * Refresh/query a single work order's full details from Aquasis
 * @param {string} orderCode - Order origin ID (e.g., 'O4514415')
 * @param {string} operationalSiteID - Exploitation code (e.g., '8' — will be padded to '0008')
 * @returns {Promise<Document>} XML document with full OT data
 */
export const refreshData = async (orderCode, operationalSiteID = '') => {
  const paddedSiteID = operationalSiteID
    ? String(operationalSiteID).padStart(4, '0')
    : '';
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericaordenesservicio.occamcxf.occam.agbar.com/">
   <soapenv:Header/>
   <soapenv:Body>
      <int:refreshData>
         <otRequest>
            <operationalSiteID>${xmlEscape(paddedSiteID)}</operationalSiteID>
            <otClassID>1</otClassID>
            <otOriginID>${xmlEscape(orderCode)}</otOriginID>
            <language>es</language>
         </otRequest>
      </int:refreshData>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_WORKORDER_URL, '', xml);
};

/**
 * Refresh/query a single work order's full details as JSON
 * @param {string} orderCode - Order origin ID (e.g., 'O4514415')
 * @param {string} operationalSiteID - Exploitation code (e.g., '8')
 * @returns {Promise<any>} JSON object with otData, clientData, otElements, otReadings, otCustomerUnpaidBills, otCustomerDebt, otComments
 */
export const refreshDataJson = async (orderCode, operationalSiteID = '') => {
  const xmlDoc = await refreshData(orderCode, operationalSiteID);
  const returnElement =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('refreshDataReturn')[0] ||
    xmlDoc.getElementsByTagName('refreshDataResponse')[0];
  if (returnElement) return xmlToJson(returnElement);
  return xmlToJson(xmlDoc);
};

/**
 * Create work order
 * @param {Object} data - Work order data
 * @param {string} data.tipoOrden - Order type
 * @param {string} data.motivoOrden - Order reason
 * @param {string} data.fechaCreacionOrden - Creation date
 * @param {string} data.numContrato - Contract number
 * @param {string} data.idPtoServicio - Service point ID
 * @param {string} data.fechaEstimdaFin - Estimated end date
 * @param {string} data.observaciones - Observations
 * @param {string} data.codigoReparacion - Repair code
 * @param {string} data.anyoExpediente - File year
 * @returns {Promise<Document>} XML document
 */
const sanitizeObservacionesAlphanumeric = str =>
  (str || '').replace(/[^a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ.,;:\-¿?¡!()"']/g, '');

export const crearOrdenTrabajo = async data => {
  const MAX_OBS_LENGTH = 80;
  const safeObservaciones = sanitizeObservacionesAlphanumeric(
    data.observaciones || ''
  ).substring(0, MAX_OBS_LENGTH);

  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericaordenesservicio.occamcxf.occam.agbar.com/" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <int:crearOrdenTrabajo>
         <idioma>es</idioma>
         <ordenTrabajo>
            <tipoOrden>${xmlEscape(data.tipoOrden)}</tipoOrden>
            <motivoOrden>${xmlEscape(data.motivoOrden)}</motivoOrden>
            <fechaCreacionOrden>${xmlEscape(data.fechaCreacionOrden)}</fechaCreacionOrden>
            <numContrato>${xmlEscape(data.numContrato)}</numContrato>
            <idPtoServicio>${xmlEscape(data.idPtoServicio)}</idPtoServicio>
            <fechaEstimdaFin>${xmlEscape(data.fechaEstimdaFin)}</fechaEstimdaFin>
            <observaciones>${xmlEscape(safeObservaciones)}</observaciones>
       <codigoObsCambCont></codigoObsCambCont>
            <codigoReparacion>${xmlEscape(data.codigoReparacion)}</codigoReparacion>
            <anyoExpediente>${xmlEscape(data.anyoExpediente)}</anyoExpediente>
       <numeroExpediente></numeroExpediente>
            <instalaValvulaPaso>0</instalaValvulaPaso>
         </ordenTrabajo>
         <enCurso>0</enCurso>
      </int:crearOrdenTrabajo>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_WORKORDER_URL, '', xml);
};

/**
 * Create external work order (for tickets without contract number)
 * @param {Object} data - External order data
 * @param {string} data.explotacionSGO - Exploitation code (e.g., '01')
 * @param {string} data.idExplotacion - Exploitation ID (e.g., '1')
 * @param {string} data.fechaCreacion - Creation date ISO string
 * @param {string} data.estado - Order status (e.g., '1' for new)
 * @param {string} data.idPtoServicio - Service point ID (e.g., '0')
 * @param {string} data.numContrato - Contract number (e.g., '0')
 * @param {string} data.numContador - Meter number (e.g., '')
 * @param {string} data.idSGO - SGO identifier (ticket folio)
 * @param {string} data.tipoOrden - Order type code
 * @param {string} data.motivoNoResolucion - Non-resolution reason
 * @param {string} data.telelectura - Remote reading flag ('true'/'false')
 * @param {string} data.textoDireccion - Address text
 * @param {string} data.idPoblacion - Population ID
 * @param {string} data.idLocalidad - Locality ID
 * @param {string} data.idCalle - Street ID
 * @param {string} data.numero - Street number
 * @returns {Promise<Document>} XML document
 */
export const crearOrdenExterna = async data => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericaordenesservicio.occamcxf.occam.agbar.com/" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <int:crearOrdenExterna>
         <ordenTrabajo>
            <datos>
               <explotacionSGO>${xmlEscape(data.explotacionSGO)}</explotacionSGO>
               <idExplotacion>${xmlEscape(data.idExplotacion)}</idExplotacion>
               <fechaCreacion>${xmlEscape(data.fechaCreacion)}</fechaCreacion>
               <estado>${xmlEscape(data.estado)}</estado>
               <idPtoServicio>${xmlEscape(data.idPtoServicio)}</idPtoServicio>
               <numContrato>${xmlEscape(data.numContrato)}</numContrato>
               <numContador>${xmlEscape(data.numContador)}</numContador>
               <idSGO>${xmlEscape(data.idSGO)}</idSGO>
               <tipoOrden>${xmlEscape(data.tipoOrden)}</tipoOrden>
               <motivoNoResolucion>${xmlEscape(data.motivoNoResolucion)}</motivoNoResolucion>
               <telelectura>${xmlEscape(data.telelectura)}</telelectura>
            </datos>
            <direccion>
               <textoDireccion>${xmlEscape(data.textoDireccion)}</textoDireccion>
               <idPoblacion>${xmlEscape(data.idPoblacion)}</idPoblacion>
               <idLocalidad>${xmlEscape(data.idLocalidad)}</idLocalidad>
               <idCalle>${xmlEscape(data.idCalle)}</idCalle>
               <numero>${xmlEscape(data.numero)}</numero>
            </direccion>
         </ordenTrabajo>
      </int:crearOrdenExterna>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_WORKORDER_URL, 'crearOrdenExterna', xml);
};

/**
 * Create external work order and return parsed JSON response
 * @param {Object} data - Same as crearOrdenExterna
 * @returns {Promise<{aquasisOrderId: string, ordenCreada: string, codigoError: string, descripcionError: string}>}
 */
export const crearOrdenExternaJson = async data => {
  const xmlDoc = await crearOrdenExterna(data);
  const returnEl =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('crearOrdenExternaReturn')[0] ||
    xmlDoc.getElementsByTagName('crearOrdenExternaResponse')[0];

  if (!returnEl) {
    throw new Error('No response element found in crearOrdenExterna response');
  }

  const json = xmlToJson(returnEl);
  const codigoError = json?.resultado?.codigoError ?? json?.codigoError ?? '';
  const descripcionError =
    json?.resultado?.descripcionError ?? json?.descripcionError ?? '';
  const aquasisOrderId = json?.idOrden ?? '';
  const ordenCreada = json?.ordenCreada ?? '';

  if (codigoError !== '0') {
    throw new Error(
      `crearOrdenExterna error (${codigoError}): ${descripcionError}`
    );
  }

  return { aquasisOrderId, ordenCreada, codigoError, descripcionError };
};

/**
 * Resolve work order
 * @param {Object} data - Resolution data
 * @returns {Promise<Document>} XML document
 */
export const resolveOT = async data => {
  const elements = Array.isArray(data.resolutionElements)
    ? data.resolutionElements
    : [];
  const elementsXml = elements
    .map(
      el => `
            <otResolutionElements>
               <installedOrRetired>${xmlEscape(el.installedOrRetired ?? false)}</installedOrRetired>
               <meterDial>${xmlEscape(el.meterDial ?? 0)}</meterDial>
               <meterBrandID>${xmlEscape(el.meterBrandID ?? '')}</meterBrandID>
               <meterModel>${xmlEscape(el.meterModel ?? '')}</meterModel>
               <meterGauge>${xmlEscape(el.meterGauge ?? '')}</meterGauge>
               <serialNumber>${xmlEscape(el.serialNumber ?? '')}</serialNumber>
               <communicationModule>${xmlEscape(el.communicationModule ?? '')}</communicationModule>
               <manufacturedYear>${xmlEscape(el.manufacturedYear ?? 0)}</manufacturedYear>
               <installationDate>${xmlEscape(el.installationDate ?? '')}</installationDate>
               <emplacement>${xmlEscape(el.emplacement ?? '')}</emplacement>
               <key>${xmlEscape(el.key ?? 0)}</key>
               <batteryRow>${xmlEscape(el.batteryRow ?? 0)}</batteryRow>
               <batteryColumn>${xmlEscape(el.batteryColumn ?? 0)}</batteryColumn>
               <dateReading>${xmlEscape(el.dateReading ?? '')}</dateReading>
               <readingRegister>${xmlEscape(el.readingRegister ?? 0)}</readingRegister>
               <executorIdentifier>${xmlEscape(el.executorIdentifier ?? '')}</executorIdentifier>
               <executorName>${xmlEscape(el.executorName ?? '')}</executorName>
               <valveRetention>${xmlEscape(el.valveRetention ?? false)}</valveRetention>
               <meterInPlace>${xmlEscape(el.meterInPlace ?? '')}</meterInPlace>
               <companyExecutorIdentifier>${xmlEscape(el.companyExecutorIdentifier ?? '')}</companyExecutorIdentifier>
               <companyExecutorName>${xmlEscape(el.companyExecutorName ?? '')}</companyExecutorName>
            </otResolutionElements>`
    )
    .join('');

  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericaordenesservicio.occamcxf.occam.agbar.com/" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <int:resolveOT>
         <otResolution>
            <otResolutionData>
               <operationalSiteID>${xmlEscape(data.operationalSiteID)}</operationalSiteID>
               <installationID>${xmlEscape(data.installationID)}</installationID>
               <systemOrigin>${xmlEscape(data.systemOrigin)}</systemOrigin>
               <otClass>${xmlEscape(data.otClass)}</otClass>
               <otOrigin>${xmlEscape(data.otOrigin)}</otOrigin>
               <endDateOt>${xmlEscape(data.endDateOt)}</endDateOt>
               <endLastTaskOt>${xmlEscape(data.endLastTaskOt)}</endLastTaskOt>
               <finalSolution>${xmlEscape(data.finalSolution)}</finalSolution>
               <nonExecutionMotive>${xmlEscape(data.nonExecutionMotive)}</nonExecutionMotive>
               <solutionDescription>${xmlEscape(data.solutionDescription)}</solutionDescription>
               <executorIdentifier>${xmlEscape(data.executorIdentifier)}</executorIdentifier>
               <executorName>${xmlEscape(data.executorName)}</executorName>
               <companyExecutorIdentifier>${xmlEscape(data.companyExecutorIdentifier)}</companyExecutorIdentifier>
               <companyExecutorName>${xmlEscape(data.companyExecutorName)}</companyExecutorName>
               <transmitterInstalled>${xmlEscape(data.transmitterInstalled)}</transmitterInstalled>
               <language>${xmlEscape(data.language)}</language>
               <suspensionLevel>${xmlEscape(data.suspensionLevel)}</suspensionLevel>
               <geolocalization>
                  <longitude>${xmlEscape(data.longitude)}</longitude>
                  <latitude>${xmlEscape(data.latitude)}</latitude>
                  <coordinatesType>${xmlEscape(data.coordinatesType)}</coordinatesType>
                  <codificationType>${xmlEscape(data.codificationType)}</codificationType>
                  <captureDate>${xmlEscape(data.captureDate)}</captureDate>
               </geolocalization>
            </otResolutionData>${elementsXml}
         </otResolution>
      </int:resolveOT>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_WORKORDER_URL, '', xml);
};

/**
 * Report visit
 * @param {Object} data - Visit data
 * @returns {Promise<Document>} XML document
 */
export const informarVisita = async data => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericaordenesservicio.occamcxf.occam.agbar.com/" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <int:informarVisita>
         <id>${xmlEscape(data.id)}</id>
         <codOrden>${xmlEscape(data.codOrden)}</codOrden>
         <fechaVisita>${xmlEscape(data.fechaVisita)}</fechaVisita>
         <resultado>${xmlEscape(data.resultado)}</resultado>
         <idOperario>${xmlEscape(data.idOperario)}</idOperario>
         <nombreOperario>${xmlEscape(data.nombreOperario)}</nombreOperario>
         <cifContratista>${xmlEscape(data.cifContratista)}</cifContratista>
         <nombreContratista>${xmlEscape(data.nombreContratista)}</nombreContratista>
         <codIncidencia>${xmlEscape(data.codIncidencia)}</codIncidencia>
         <descIncidencia>${xmlEscape(data.descIncidencia)}</descIncidencia>
         <observaciones>${xmlEscape(data.observaciones)}</observaciones>
         <aResponsable>
            <codVinculacion>${xmlEscape(data.codVinculacion)}</codVinculacion>
            <idDocFirma>${xmlEscape(data.idDocFirma)}</idDocFirma>
            <personaVisita>
               <nombre>${xmlEscape(data.personaNombre)}</nombre>
               <apellido1>${xmlEscape(data.personaApellido1)}</apellido1>
               <apellido2>${xmlEscape(data.personaApellido2)}</apellido2>
               <telefono>${xmlEscape(data.personaTelefono)}</telefono>
               <nif>${xmlEscape(data.personaNif)}</nif>
            </personaVisita>
         </aResponsable>
      </int:informarVisita>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_WORKORDER_URL, '', xml);
};

/**
 * Report visit and return parsed result
 * @param {Object} data - Visit data
 * @returns {Promise<{visitId: string|null}>} Parsed visit ID
 */
export const informarVisitaJson = async data => {
  const xmlDoc = await informarVisita(data);
  const returnEl =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('informarVisitaReturn')[0] ||
    xmlDoc.getElementsByTagName('informarVisitaResponse')[0];

  if (returnEl) {
    if (returnEl.children && returnEl.children.length > 0) {
      const json = xmlToJson(returnEl);
      return {
        visitId:
          json?.idVisita || json?.id || returnEl.textContent?.trim() || null,
      };
    }
    return { visitId: returnEl.textContent?.trim() || null };
  }
  return { visitId: null };
};

/**
 * Resolve work order and return parsed result
 * @param {Object} data - Resolution data
 * @returns {Promise<{success: boolean}>} Whether resolution succeeded
 */
export const resolveOTJson = async data => {
  const xmlDoc = await resolveOT(data);
  const returnEl =
    xmlDoc.getElementsByTagName('return')[0] ||
    xmlDoc.getElementsByTagName('resolveOTReturn')[0] ||
    xmlDoc.getElementsByTagName('resolveOTResponse')[0];

  const resultText = returnEl?.textContent?.trim()?.toLowerCase();
  return { success: resultText === 'true' || resultText === '1' };
};

// ============================================================================
// SOAP API Functions - Meters
// ============================================================================

/**
 * Get service point by meter
 * @param {string} listaNumSerieContador - Meter serial numbers list
 * @param {string} usuario - User
 * @param {string} idioma - Language (default: 'es')
 * @param {string} opciones - Options (default: '')
 * @returns {Promise<Document>} XML document
 */
export const getPuntoServicioPorContador = async (
  listaNumSerieContador,
  usuario,
  idioma = 'es',
  opciones = ''
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericacontadores.occamcxf.occam.agbar.com/">
   <soapenv:Header/>
   <soapenv:Body>
      <int:getPuntoServicioPorContador>
         <listaNumSerieContador>${xmlEscape(listaNumSerieContador)}</listaNumSerieContador>
         <usuario>${xmlEscape(usuario)}</usuario>
         <idioma>${xmlEscape(idioma)}</idioma>
         <opciones>${xmlEscape(opciones)}</opciones>
      </int:getPuntoServicioPorContador>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_METER_URL, '', xml);
};

// ============================================================================
// SOAP API Functions - Debt
// ============================================================================

/**
 * Get debt information
 * @param {string} tipoIdentificador - Identifier type
 * @param {string} valor - Value
 * @param {string} explotacion - Exploitation
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getDeuda = async (
  tipoIdentificador,
  valor,
  explotacion,
  idioma = 'es'
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericagestiondeuda.occamcxf.occam.agbar.com/" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
  <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <int:getDeuda>
         <tipoIdentificador>${xmlEscape(tipoIdentificador)}</tipoIdentificador>
         <valor>${xmlEscape(valor)}</valor>
         <explotacion>${xmlEscape(explotacion)}</explotacion>
         <idioma>${xmlEscape(idioma)}</idioma>
      </int:getDeuda>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_DEBT_URL, '', xml);
};

/**
 * Get debt information as JSON
 * @param {string} tipoIdentificador - Identifier type
 * @param {string} valor - Value
 * @param {string} explotacion - Exploitation
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<any>} JSON object
 */
export const getDeudaJson = async (
  tipoIdentificador,
  valor,
  explotacion,
  idioma = 'es'
) => {
  const xmlDoc = await getDeuda(tipoIdentificador, valor, explotacion, idioma);
  const returnElement =
    xmlDoc.getElementsByTagName('getDeudaReturn')[0] ||
    xmlDoc.getElementsByTagName('getDeudaResponse')[0];
  if (returnElement) return xmlToJson(returnElement);
  return xmlToJson(xmlDoc);
};

/**
 * Get total debt with invoice breakdown (resilient to billing cycle state)
 * Uses InterfazGenericaGestionDeudaWS - same service as getDeuda
 * @param {string} contrato - Contract number
 * @param {string} explotacion - Exploitation code
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getDeudaTotalConFacturas = async (
  contrato,
  explotacion,
  idioma = 'es'
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:int="http://interfazgenericagestiondeuda.occamcxf.occam.agbar.com/" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
  <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <int:getDeudaTotalConFacturas>
         <contrato>${xmlEscape(contrato)}</contrato>
         <explotacion>${xmlEscape(explotacion)}</explotacion>
         <idioma>${xmlEscape(idioma)}</idioma>
      </int:getDeudaTotalConFacturas>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_DEBT_URL, '', xml);
};

/**
 * Get total debt with invoice breakdown as JSON
 * @param {string} contrato - Contract number
 * @param {string} explotacion - Exploitation code
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<any>} JSON object with deudaTotal, facturas, cantidadFacturas
 */
export const getDeudaTotalConFacturasJson = async (
  contrato,
  explotacion,
  idioma = 'es'
) => {
  const xmlDoc = await getDeudaTotalConFacturas(contrato, explotacion, idioma);
  const returnElement =
    xmlDoc.getElementsByTagName('getDeudaTotalConFacturasReturn')[0] ||
    xmlDoc.getElementsByTagName('getDeudaTotalConFacturasResponse')[0] ||
    xmlDoc.getElementsByTagName('return')[0];
  if (returnElement) return xmlToJson(returnElement);
  return xmlToJson(xmlDoc);
};

// ============================================================================
// SOAP API Functions - Readings
// ============================================================================

/**
 * Get readings
 * @param {string} explotacion - Exploitation
 * @param {string} contrato - Contract
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getLecturas = async (explotacion, contrato, idioma = 'es') => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
      <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
   </soapenv:Header>
   <soapenv:Body>
      <occ:getLecturas>
      <explotacion>${xmlEscape(explotacion)}</explotacion>
      <contrato>${xmlEscape(contrato)}</contrato>
      <idioma>${xmlEscape(idioma)}</idioma>
      </occ:getLecturas>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_READINGS_URL, '', xml);
};

/**
 * Get consumption data
 * @param {string} explotacion - Exploitation
 * @param {string} contrato - Contract
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getConsumos = async (explotacion, contrato, idioma = 'es') => {
  // Ensure correct format with leading zeros if needed
  const explotacionFormatted = explotacion.padStart(2, '0');

  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
      <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
   </soapenv:Header>
   <soapenv:Body>
      <occ:getConsumos>
      <explotacion>${xmlEscape(explotacionFormatted)}</explotacion>
      <contrato>${xmlEscape(contrato)}</contrato>
      <idioma>${xmlEscape(idioma)}</idioma>
      </occ:getConsumos>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_READINGS_URL, '', xml);
};

/**
 * Get concepts
 * @param {string} explotacion - Exploitation
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getConceptos = async (explotacion, idioma = 'es') => {
  // Ensure correct format with leading zeros
  const explotacionFormatted = explotacion.padStart(2, '0');

  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com">
   <soapenv:Header/>
   <soapenv:Body>
      <occ:getConceptos>
         <explotacion>${xmlEscape(explotacionFormatted)}</explotacion>
         <idioma>${xmlEscape(idioma)}</idioma>
      </occ:getConceptos>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_READINGS_URL, '', xml);
};

/**
 * Get water rate by contract
 * @param {string} explotacion - Exploitation
 * @param {string} contrato - Contract
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getTarifaDeAguaPorContrato = async (
  explotacion,
  contrato,
  idioma = 'es'
) => {
  // Ensure correct format with leading zeros
  const explotacionFormatted = explotacion.padStart(2, '0');

  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com">
   <soapenv:Header/>
   <soapenv:Body>
      <occ:getTarifaDeAguaPorContrato>
         <explotacion>${xmlEscape(explotacionFormatted)}</explotacion>
         <contrato>${xmlEscape(contrato)}</contrato>
         <idioma>${xmlEscape(idioma)}</idioma>
      </occ:getTarifaDeAguaPorContrato>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_READINGS_URL, '', xml);
};

/**
 * Get water rate by contract as JSON
 * @param {string} explotacion - Exploitation
 * @param {string} contrato - Contract
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<any>} JSON object
 */
export const getTarifaDeAguaPorContratoJson = async (
  explotacion,
  contrato,
  idioma = 'es'
) => {
  const xmlDoc = await getTarifaDeAguaPorContrato(
    explotacion,
    contrato,
    idioma
  );
  const returnElement =
    xmlDoc.getElementsByTagName('getTarifaDeAguaPorContratoReturn')[0] ||
    xmlDoc.getElementsByTagName('getTarifaDeAguaPorContratoResponse')[0] ||
    xmlDoc.getElementsByTagName('return')[0];

  if (returnElement) {
    return xmlToJson(returnElement);
  }
  return xmlToJson(xmlDoc);
};

/**
 * Get consumption data for charts
 * @param {string} explotacion - Exploitation
 * @param {string} contrato - Contract
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document
 */
export const getConsumosParaGraficas = async (
  explotacion,
  contrato,
  idioma = 'es'
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com">
   <soapenv:Header/>
   <soapenv:Body>
      <occ:getConsumosParaGraficas>
         <explotacion>${xmlEscape(explotacion)}</explotacion>
         <contrato>${xmlEscape(contrato)}</contrato>
         <idioma>${xmlEscape(idioma)}</idioma>
      </occ:getConsumosParaGraficas>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_READINGS_URL, '', xml);
};

// ============================================================================
// SOAP API Functions - Receipts
// ============================================================================

/**
 * Change notification email for person
 * @param {string} nif - NIF
 * @param {string} nombre - Name
 * @param {string} apellido1 - First surname
 * @param {string} apellido2 - Second surname
 * @param {string} contrato - Contract
 * @param {string} emailAntiguo - Old email
 * @param {string} emailNuevo - New email
 * @param {string} codigoOficina - Office code
 * @param {string} usuario - User
 * @returns {Promise<Document>} XML document
 */
export const cambiarEmailNotificacionPersona = async (
  nif,
  nombre,
  apellido1,
  apellido2,
  contrato,
  emailAntiguo,
  emailNuevo,
  codigoOficina,
  usuario
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com">
   <soapenv:Header/>
   <soapenv:Body>
      <occ:cambiarEmailNotificacionPersona>
      <nif>${xmlEscape(nif)}</nif>
      <nombre>${xmlEscape(nombre)}</nombre> 
      <apellido1>${xmlEscape(apellido1)}</apellido1>
      <apellido2>${xmlEscape(apellido2)}</apellido2>
      <contrato>${xmlEscape(contrato)}</contrato>
      <emailAntigo>${xmlEscape(emailAntiguo)}</emailAntigo>
      <emailNuevo>${xmlEscape(emailNuevo)}</emailNuevo>
         <atencionDe>ChatBot</atencionDe>
         <codigoOficina>${xmlEscape(codigoOficina)}</codigoOficina>
         <usuario>${xmlEscape(usuario)}</usuario>
      </occ:cambiarEmailNotificacionPersona>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_RECEIPT_URL, '', xml);
};

/**
 * Change notification person for contract
 * @param {string} contrato - Contract
 * @param {string} nif - NIF
 * @param {string} email1 - Email 1
 * @param {string} email2 - Email 2
 * @param {string} usuario - User
 * @returns {Promise<Document>} XML document
 */
export const cambiarPersonaNotificacionContrato = async (
  contrato,
  nif,
  email1,
  email2,
  usuario
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <occ:cambiarPersonaNotificacionContrato>
         <contrato>${xmlEscape(contrato)}</contrato>
         <nif>${xmlEscape(nif)}</nif> 
         <email1>${xmlEscape(email1)}</email1> 
         <email2>${xmlEscape(email2)}</email2>
         <usuario>${xmlEscape(usuario)}</usuario> 
      </occ:cambiarPersonaNotificacionContrato>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_RECEIPT_URL, '', xml);
};

/**
 * Change invoice type for contract
 * @param {string} contrato - Contract
 * @param {string} nif - NIF
 * @param {string} tipoFactura - Invoice type
 * @param {string} usuario - User (default: '0000004874')
 * @returns {Promise<Document>} XML document
 */
export const cambiarTipoFacturaContrato = async (
  contrato,
  nif,
  tipoFactura,
  usuario = '0000004874'
) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
    <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
  </soapenv:Header>
   <soapenv:Body>
      <occ:cambiarTipoFacturaContrato>
         <contrato>${xmlEscape(contrato)}</contrato>
         <nif>${xmlEscape(nif)}</nif> 
         <tipoFactura>${xmlEscape(tipoFactura)}</tipoFactura>
         <usuario>${xmlEscape(usuario)}</usuario>
      </occ:cambiarTipoFacturaContrato>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_RECEIPT_URL, '', xml);
};

// ============================================================================
// SOAP API Functions - Invoices (Facturas)
// ============================================================================

/**
 * Get invoices list for a contract
 * @param {string} explotacion - Exploitation code
 * @param {string} contrato - Contract number
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<Document>} XML document with invoices list
 */
export const getFacturas = async (explotacion, contrato, idioma = 'es') => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
      <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
   </soapenv:Header>
   <soapenv:Body>
      <occ:getFacturas>
         <explotacion>${xmlEscape(explotacion)}</explotacion>
         <contrato>${xmlEscape(contrato)}</contrato>
         <idioma>${xmlEscape(idioma)}</idioma>
      </occ:getFacturas>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_RECEIPT_URL, '', xml);
};

/**
 * Get invoices list as JSON
 * @param {string} explotacion - Exploitation code
 * @param {string} contrato - Contract number
 * @param {string} idioma - Language (default: 'es')
 * @returns {Promise<any>} JSON object with invoices
 */
export const getFacturasJson = async (explotacion, contrato, idioma = 'es') => {
  const xmlDoc = await getFacturas(explotacion, contrato, idioma);
  const returnElement =
    xmlDoc.getElementsByTagName('getFacturasReturn')[0] ||
    xmlDoc.getElementsByTagName('getFacturasResponse')[0] ||
    xmlDoc.getElementsByTagName('return')[0];

  if (returnElement) {
    return xmlToJson(returnElement);
  }
  return xmlToJson(xmlDoc);
};

// ============================================================================
// SOAP API Functions - PDF Invoices
// ============================================================================

/**
 * Get PDF invoice from Aquacis (returns base64 encoded PDF)
 * Uses InterfazGenericaContratacionWS endpoint
 * @param {string} numFactura - Invoice number (e.g., "000120231000049814")
 * @param {string} numContrato - Contract number (e.g., "442761")
 * @returns {Promise<Document>} XML document with base64 PDF in getPdfFacturaReturn
 */
export const getPdfFactura = async (numFactura, numContrato) => {
  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
   <soapenv:Header>
      <wsse:Security mustUnderstand="1">
        <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
          <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
          <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
        </wsse:UsernameToken>
      </wsse:Security>
   </soapenv:Header>
   <soapenv:Body>
      <occ:getPdfFactura>
         <numFactura>${xmlEscape(numFactura)}</numFactura>
         <numContrato>${xmlEscape(numContrato)}</numContrato>
      </occ:getPdfFactura>
   </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_CONTRACT_URL, '', xml);
};

/**
 * Get PDF invoice as base64 string
 * @param {string} numFactura - Invoice number (e.g., "000120231000049814")
 * @param {string} numContrato - Contract number (e.g., "442761")
 * @returns {Promise<{base64: string, numFactura: string, numContrato: string}>} Base64 encoded PDF data
 */
export const getPdfFacturaBase64 = async (numFactura, numContrato) => {
  const xmlDoc = await getPdfFactura(numFactura, numContrato);

  // Try different possible tag names for the PDF data
  const pdfElement =
    xmlDoc.getElementsByTagName('pdf')[0] ||
    xmlDoc.getElementsByTagName('getPdfFacturaReturn')[0] ||
    xmlDoc.getElementsByTagName('getPdfFacturaResponse')[0] ||
    xmlDoc.getElementsByTagName('return')[0];

  if (pdfElement) {
    const rawBase64 = pdfElement.textContent || '';

    // Keep only valid base64 characters: A-Z, a-z, 0-9, +, /, =
    const base64 = rawBase64.replace(/[^A-Za-z0-9+/=]/g, '');

    if (base64 && base64.length > 0) {
      return {
        base64,
        numFactura,
        numContrato,
        filename: `factura_${numFactura}.pdf`,
      };
    }
  }

  throw new Error('No PDF data found in SOAP response');
};

/**
 * Get PDF invoice as binary Blob
 * @param {string} numFactura - Invoice number
 * @param {string} numContrato - Contract number
 * @returns {Promise<{blob: Blob, filename: string}>} PDF Blob and filename
 */
export const getPdfFacturaBlob = async (numFactura, numContrato) => {
  const { base64, filename } = await getPdfFacturaBase64(
    numFactura,
    numContrato
  );

  // Convert base64 to binary blob
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });

  return { blob, filename };
};

/**
 * Download PDF invoice directly in browser
 * @param {string} numFactura - Invoice number
 * @param {string} numContrato - Contract number
 */
export const downloadPdfFactura = async (numFactura, numContrato) => {
  const { blob, filename } = await getPdfFacturaBlob(numFactura, numContrato);

  // Trigger browser download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Send meter reading to Aquasis (solicitudIntroduccionLectura)
 * @param {string|number} lecturaContador1 - Meter reading value
 * @param {string} numeroContrato - Contract number
 * @returns {Promise<Document>} XML response document
 */
export const introduccionLectura = async ({ lecturaContador1, numeroContrato }) => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const fechaActual = `${dd}${mm}${yyyy}`;

  const xml = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:occ="http://occamWS.ejb.negocio.occam.agbar.com" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
  <soapenv:Header>
    <wsse:Security mustUnderstand="1">
      <wsse:UsernameToken wsu:Id="UsernameToken-${xmlEscape(CEA_API_USERNAME)}">
        <wsse:Username>${xmlEscape(CEA_API_USERNAME)}</wsse:Username>
        <wsse:Password Type="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-username-token-profile-1.0#PasswordText">${xmlEscape(CEA_API_PASSWORD)}</wsse:Password>
      </wsse:UsernameToken>
    </wsse:Security>
  </soapenv:Header>
  <soapenv:Body>
    <occ:solicitudIntroduccionLectura>
      <lecturaContador1>${xmlEscape(String(lecturaContador1))}</lecturaContador1>
      <lecturaContador2></lecturaContador2>
      <numeroContrato>${xmlEscape(String(numeroContrato))}</numeroContrato>
      <fechaActual>${fechaActual}</fechaActual>
    </occ:solicitudIntroduccionLectura>
  </soapenv:Body>
</soapenv:Envelope>`;

  return sendSoapRequest(CEA_SOAP_CONTRACT_URL, 'solicitudIntroduccionLectura', xml);
};
